import type { UserModel } from '@/apiTypes/UserTypes';
import { isAccountSetupIncomplete } from '@/helpers/profileHelpers';

const ANONYMOUS_ID_STORAGE_KEY = 'gozig.productFruitsAnonymousId';

export interface ProductFruitsWorkspace {
    workspaceCode: string;
    user: {
        username: string;
        props?: { [key: string]: string | number | boolean };
        hmac?: { hash: string };
    };
}

/**
 * Stable per-browser id for the pre-login workspace, where there is no user yet. A new browser,
 * device or cleared storage gets a new id, so that visitor counts as a new Product Fruits user.
 * Falls back to a per-page-load id when storage is unavailable (private windows, blocked site data).
 */
export const getAnonymousId = (): string => {
    try {
        const stored = window.localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
        if (stored) {
            return stored;
        }
        const id = crypto.randomUUID();
        window.localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, id);
        return id;
    }
    catch {
        return crypto.randomUUID();
    }
};

/**
 * Which Product Fruits workspace to run, or null for none. Any logged-in user (including one still
 * in account setup) gets the post-login workspace, identified by the API's Product Fruits username
 * (an HMAC of their internal id) and, when the API signed it, the identity verification HMAC;
 * everyone else gets the pre-login workspace. An empty code, or a missing username, turns that
 * workspace off.
 */
export const resolveProductFruitsWorkspace = (
    configs: { [key: string]: string },
    user: UserModel | undefined,
    getAnonymous: () => string = getAnonymousId,
): ProductFruitsWorkspace | null => {
    if (user?.id) {
        const workspaceCode = configs['productFruitsPostLoginCode'];
        const username = user.userProfile?.productFruitsUsername;
        if (!workspaceCode || !username) {
            return null;
        }
        const hmacHash = user.userProfile?.productFruitsHmac;
        return {
            workspaceCode,
            user: {
                username,
                props: {
                    accountSetupComplete: !isAccountSetupIncomplete(user),
                    betaOptIn: user.userProfile?.betaOptIn === true,
                },
                ...(hmacHash ? { hmac: { hash: hmacHash } } : {}),
            },
        };
    }

    const workspaceCode = configs['productFruitsPreLoginCode'];
    if (!workspaceCode) {
        return null;
    }
    return { workspaceCode, user: { username: getAnonymous() } };
};
